<script setup lang="ts">
import { RemovableRef, useStorage } from "@vueuse/core";
import {
  CheckboxGroupValueType,
  ElMessage,
  type FormInstance,
  type FormRules,
} from "element-plus";
import {
  computed,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  Ref,
  watch,
  watchEffect,
} from "vue";
import { useRoute } from "vue-router";
import {
  CalcInputs,
  FinderResultDto,
  HousingApplication,
  HousingApplicationSource,
  HousingApplicationStatus,
  HousingFinderInput,
  HousingSiteDto,
  HousingSiteResult,
  HousingSiteResultType,
  TreeType,
  type TreeNodeDto,
} from "~/api";
import SectorFinder from "~/components/SectorFinder.vue";
import { useSectorTree } from "~/composables/useSectorTree";
import { useUnitTypeTree } from "~/composables/useUnitTypeTree";
import { applicationRouteMap } from "~/router/mainRouter";
import { getEnvConfig } from "~/services/appConfig";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import { formatDate } from "~/utils/dateUtils";
import { showError } from "~/utils/handleAPIError";
import { useHousingOrgStore } from "../../stores/HousingOrgStore";
import { useHousingSiteDetailStore } from "../../stores/HousingSiteDetailStore";
import { useHousingSiteStore } from "../../stores/HousingSiteStore";
import FilterBox from "../components/FilterBox.vue";
import Location from "../components/Location.vue";
import {
  HousingFilters,
  LocationInfo,
  QuadrantInfo,
  ResultPlus,
  RuleForm,
} from "../types/plusTypes";

// Cache the environment config for performance
const envConfig = getEnvConfig();

const route = useRoute();

const emit = defineEmits<{
  (e: "saveFinderInfo", finderInputs: HousingFinderInput): void;
  (e: "updateFinderInfo", finderInputs: HousingFinderInput): void;
  (e: "updatePreferences", preferences: string[]): void;
  (e: "setSites", sites: HousingSiteDto[]): void;
  (e: "updateSitesQualifiedFor", sitesQualifiedFor: HousingSiteResult[]): void;
  (e: "confirmSelection"): void;
  (e: "updateSelection", preferences: string[]): void;
  (e: "apply"): void;
}>();

const props = defineProps<{
  inputs?: HousingFinderInput;
  application?: HousingApplication;
  favouriteSites?: string[];
  isReadOnly?: boolean;
  initialCollapseLocations?: boolean;
  initialShowOnlySelected?: boolean;
}>();

const housingOrgStore = useHousingOrgStore();
const housingSiteStore = useHousingSiteStore();
const housingSiteDetailStore = useHousingSiteDetailStore();
const myInfoStore = useMyInfoStore();

const {
  sectorTreeData,
  flatNodeMap: sectorFlatNodeMap,
  loading: sectorLoading,
  loadSectorTree,
} = useSectorTree();
const selectedSectors = ref<string[]>([]);

/**
 * Split the flat SectorFinder selection into sector-level (nodes with children)
 * vs category-level (leaf nodes). This mirrors the DB split on
 * HousingSiteUnitConfiguration (SectorNodeGuid + optional CategoryNodeGuid).
 */
const splitSelectedSectors = (): {
  sectorGuids: string[];
  categoryGuids: string[];
} => {
  const sectorGuids: string[] = [];
  const categoryGuids: string[] = [];
  for (const guid of selectedSectors.value) {
    const node = sectorFlatNodeMap.value.get(guid);
    if (node && node.children.length > 0) {
      sectorGuids.push(guid);
    } else {
      categoryGuids.push(guid);
    }
  }
  return { sectorGuids, categoryGuids };
};

const {
  loadUnitTypeTree,
  getNodeName: getUnitTypeNodeName,
  getLeafGuidByLegacyCode,
} = useUnitTypeTree();

const finderInputs = ref<HousingFinderInput>(
  props.inputs ?? ({} as HousingFinderInput),
);
const initialBuildingAttributes = ref<string[]>([
  ...new Set(props.inputs?.buildingAttributes ?? []),
]);
const loading = ref(false);
const tempHide = ref(true);

const version = "1.0.0";
const ruleFormRef = ref<FormInstance>();
const ruleForm = reactive<RuleForm>({
  couple: undefined,
  age: envConfig.defaultAge ?? 0,
  income: envConfig.defaultIncome ?? 0,
  otherAge: envConfig.defaultAge ?? 0,
  otherIncome: envConfig.defaultIncome ?? 0,
});

const minimumSingleApplicantAge = computed(() => {
  return Math.min(...housingSiteStore.List.map((site) => site.minAge ?? 55));
});

// The coapplicant age is always less than or equal to the first applicant's minimum age
const minimumCoApplicantAge = computed(() => {
  return Math.min(
    ...housingSiteStore.List.map((site) => site.coApplicantMinAge ?? 55),
  );
});

// If only one person, must be older than the minimumSingleApplicantAge
// If two, one must be minimumSingleApplicantAge+ and the other minimumCoApplicantAge+

const combinedHasMoreErrors = computed(() => {
  const firstAge = ruleForm.age;

  if (ruleForm.couple) {
    const secondAge = ruleForm.otherAge;
    if (!firstAge || !secondAge || firstAge < 10 || secondAge < 10) {
      return true;
    }
    if (!ruleForm.income || !ruleForm.otherIncome) {
      return true;
    }
  } else {
    if (!firstAge || firstAge < 10) {
      return true;
    }
    if (!ruleForm.income) {
      return true;
    }
  }

  return false;
});

const combinedError = computed(() => {
  const noError = "";
  const firstAge = ruleForm.age;

  if (ruleForm.couple) {
    const secondAge = ruleForm.otherAge;
    if (!firstAge || !secondAge || firstAge < 10 || secondAge < 10) {
      if (ageBlurredError.value) {
        return `Both applicants must be ${minimumCoApplicantAge.value}+`;
      }
      return noError;
    }
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    ageBlurredError.value = false;

    if (firstAge > 120 || secondAge > 120) {
      return "Age not accepted";
    }

    const lowestAge = Math.min(firstAge, secondAge);
    const highestAge = Math.max(firstAge, secondAge);

    const errors = [];
    if (minimumSingleApplicantAge.value === minimumCoApplicantAge.value) {
      if (
        lowestAge < minimumCoApplicantAge.value ||
        highestAge < minimumSingleApplicantAge.value
      ) {
        errors.push(
          `Both applicants must be ${minimumSingleApplicantAge.value}+`,
        );
      }
    } else {
      if (highestAge < minimumSingleApplicantAge.value) {
        errors.push(
          `One applicant must be ${minimumSingleApplicantAge.value}+`,
        );
      }

      if (lowestAge < minimumCoApplicantAge.value) {
        errors.push(
          `Youngest applicant must be ${minimumCoApplicantAge.value}+`,
        );
      }
    }

    return errors.join(", ");
  } else {
    if (!firstAge || firstAge < 10) {
      if (ageBlurredError.value) {
        return `You must be ${minimumSingleApplicantAge.value}+`;
      }
      return noError;
    }
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    ageBlurredError.value = false;

    if (firstAge > 120) {
      return "Age not accepted";
    }
    if (firstAge < minimumSingleApplicantAge.value) {
      return `You must be ${minimumSingleApplicantAge.value}+`;
    } else {
      return noError;
    }
  }
});

const ageBlurredError = ref(false);

const ageBlurred = () => {
  const firstAge = ruleForm.age;
  const secondAge = ruleForm.otherAge;
  if (ruleForm.couple) {
    if (firstAge && secondAge) {
      if (firstAge < 10 || secondAge < 10) {
        ageBlurredError.value = true;
      }
    }
  } else {
    if (firstAge) {
      if (firstAge < 10) {
        ageBlurredError.value = true;
      }
    }
  }
};

watch(
  () => ruleForm.couple,
  (newValue) => {
    if (newValue === false) {
      ruleForm.otherAge = undefined;
      ruleForm.otherIncome = undefined;
    }
  },
);

const rules = reactive<FormRules<RuleForm>>({
  couple: [
    { required: true, message: "Please choose an option", trigger: "blur" },
  ],
  age: [
    {
      required: true,
      message: "",
      type: "number",
      min: 1,
    },
  ],
  income: [
    {
      required: true,
      message: "Income required",
      trigger: "blur",
    },
    {
      type: "number",
      min: 0,
      message: "Income required",
      trigger: "blur",
    },
  ],
  otherAge: [
    {
      required: true,
      message: "",
      type: "number",
      min: 1,
    },
  ],
  otherIncome: [
    {
      required: true,
      message: "Income required",
      trigger: "blur",
    },
    {
      type: "number",
      min: 0,
      message: "Income required",
      trigger: "blur",
    },
  ],
});

const sortNameA = "Sort by Name";
const sortRentLow = "Lowest Rent First";
const sortRentHigh = "Highest Rent First";
const sortingList: string[] = [sortNameA, sortRentLow, sortRentHigh];
const defaultHousingFilters: HousingFilters = {
  sortingChosen: sortRentLow,
  favoriteChecklist: false,
  roomTypeChecklist: [],
  locationCheckList: [],
  quadrantCheckList: [],
  housingOrgChecklist: [],
  buildingTypeChecklist: [],
  buildingAttributeChecklist: [],
  accessibleOnly: false,
} as const;

const favoritesList = ref<string[]>(props.favouriteSites ?? []);
// const allFiltersApplied = ref<string[]>([]);
const surveyCompleted = ref(false);

const results = ref<ResultPlus[]>([]);
const isAllResults = ref(false);
const activeInputs = ref<string>();
const resultsHeaderRef = ref<HTMLElement>();

const buildingTypeTreeNodes = ref<TreeNodeDto[]>([]);
const buildingAttrTreeNodes = ref<TreeNodeDto[]>([]);

const loadBuildingTrees = async () => {
  await myInfoStore.load();
  const [bt, ba] = await Promise.all([
    apiClient.tree
      .getTreeDefinitionByType(
        myInfoStore.currentSysOrgGuid ?? emptyGuid,
        TreeType.BUILDING_TYPE,
      )
      .catch(() => null),
    apiClient.tree
      .getTreeDefinitionByType(
        myInfoStore.currentSysOrgGuid ?? emptyGuid,
        TreeType.BUILDING_ATTRIBUTE,
      )
      .catch(() => null),
  ]);
  buildingTypeTreeNodes.value = bt?.nodes ?? [];
  buildingAttrTreeNodes.value = ba?.nodes ?? [];
};

const flattenLeaves = (nodes: TreeNodeDto[]): TreeNodeDto[] => {
  const root = nodes.find((n) => !n.parentTreeNodeGuid) ?? nodes[0];
  return (root?.children ?? [])
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
};

const buildingOptions = computed<{ name: string; guid: string }[]>(() =>
  flattenLeaves(buildingTypeTreeNodes.value).map((n) => ({
    guid: n.guid,
    name: n.name,
  })),
);

const ACCESSIBLE_LEGACY_ATTR_NAME = "Accessible Housing (Legacy)";
const PETS_LEGACY_ATTR_NAME = "Pet Friendly (Legacy)";

const buildingAttributeOptions = computed<{ name: string; guid: string }[]>(
  () => {
    const savedAttrs = initialBuildingAttributes.value;
    return flattenLeaves(buildingAttrTreeNodes.value)
      .filter((n) => n.name !== ACCESSIBLE_LEGACY_ATTR_NAME)
      .filter(
        (n) => n.name !== PETS_LEGACY_ATTR_NAME || savedAttrs.includes(n.guid),
      )
      .map((n) => ({
        guid: n.guid,
        name: n.name.replace(" (Legacy)", ""),
      }));
  },
);

const hiddenBuildingAttrGuids = computed(() => {
  const savedAttrs = initialBuildingAttributes.value;
  const visibleGuids = new Set(
    buildingAttributeOptions.value.map((o) => o.guid),
  );
  return savedAttrs.filter((g) => !visibleGuids.has(g));
});

const roomOptions = computed(() => {
  // Flatten the V2 UnitType leaf guids from each result, dedupe, then resolve
  // labels via the UnitType tree.
  const guids = results.value
    .flatMap((result) => result.eligibleUnitTypeLeafNodeGuids ?? [])
    .filter((guid): guid is string => !!guid);

  const uniqueGuids = [...new Set(guids)];

  return uniqueGuids
    .map((guid) => ({
      label: getUnitTypeNodeName(guid) ?? "",
      value: guid,
    }))
    .filter((option) => option.label !== "");
});
const filtersAccordionState = ref([]); // Collapsed = [], Expanded = ["1"]
const housingFilters = ref<HousingFilters>({ ...defaultHousingFilters }); // use spread so we dont modify the default filters

const clearFilters = () => {
  housingFilters.value = {
    ...defaultHousingFilters,
    roomTypeChecklist: housingFilters.value.roomTypeChecklist,
    buildingAttributeChecklist: [...hiddenBuildingAttrGuids.value],
  };
};

const filterIsExpanded = computed(() => {
  return filtersAccordionState.value.length > 0;
});

const originalLocations = ref<LocationInfo[]>([]);
const originalQuadrants = ref<QuadrantInfo[]>([]);

const locations = computed(() => {
  return originalLocations.value.filter((value, index, self) => {
    return (
      self.findIndex((loc) => {
        if (loc.location === value.location && loc.minAge > value.minAge) {
          loc.minAge = value.minAge;
        }
        return loc.location === value.location;
      }) === index
    );
  });
});

const quadrants = computed(() => {
  return originalQuadrants.value.filter((value, index, self) => {
    return self.findIndex((loc) => loc.quadrant === value.quadrant) === index;
  });
});

onMounted(async () => {
  // this code prevents the calculator inputs box and the finder heading
  // from quickly appearing and then disappearing when the page is loaded
  if (
    !(
      route.name === applicationRouteMap.Intake.name ||
      route.name === applicationRouteMap.Intake.children.Finder.name
    )
  ) {
    // add class to hide the calculator inputs box and the finder heading
    const calcBox = document.getElementsByClassName("calculator-box")[0];
    const finderHeading = document.getElementsByClassName("finder-heading")[0];
    calcBox?.classList.add("hide-calcInputs");
    finderHeading?.classList.add("hide-calcInputs");

    // remove the hide-calcInputs class after a few ms
    setTimeout(() => {
      calcBox?.classList.remove("hide-calcInputs");
      finderHeading?.classList.remove("hide-calcInputs");
    }, 400);
  }

  await myInfoStore.load();
  await Promise.all([
    housingOrgStore.load(),
    housingSiteStore.load(),
    housingSiteDetailStore.load(),
    loadSectorTree(),
    loadUnitTypeTree(),
    loadBuildingTrees(),
  ]);

  originalLocations.value = housingSiteStore.List.map((site) => {
    return {
      location: site.location?.trim() ?? "",
      disabled: false,
      quadrant: site.quadrant?.trim() ?? "",
      minAge: site.minAge ?? 0,
      display: "",
    };
  });
  emit("setSites", housingSiteStore.List);
  sortLocationNames();

  originalQuadrants.value = housingSiteStore.List.map((site) => {
    return {
      quadrant: site.quadrant?.trim() ?? "",
      disabled: false,
    };
  });
  ruleForm.couple = false;

  if (props.inputs) {
    ruleForm.age = props.inputs.age1;
    ruleForm.income = props.inputs.income1;
    ruleForm.couple = false;

    if (props.inputs.age2 && props.inputs.income2) {
      ruleForm.otherAge = props.inputs.age2;
      ruleForm.otherIncome = props.inputs.income2;
      ruleForm.couple = true;
    }

    // Prefer the V2 guid list. For un-migrated rows, map legacy codes to guids
    // via the UnitType tree so the user's prior selection is still pre-checked.
    const v2 = props.inputs.roomPreferenceV2 ?? [];
    const resolvedRoomPreference =
      v2.length > 0
        ? v2
        : (props.inputs.roomPreference ?? [])
            .map((code) => getLeafGuidByLegacyCode(code))
            .filter((guid): guid is string => !!guid);

    housingFilters.value = {
      ...defaultHousingFilters,
      locationCheckList: props.inputs.locations ?? [],
      housingOrgChecklist: props.inputs.housingOrgs ?? [],
      roomTypeChecklist: resolvedRoomPreference,
      buildingTypeChecklist: props.inputs.buildingTypes ?? [],
      buildingAttributeChecklist: initialBuildingAttributes.value,
      favoriteChecklist: props.initialShowOnlySelected ?? false,
      accessibleOnly: props.inputs.accessible ?? false,
    };
    selectedSectors.value = [
      ...(props.inputs.sectorNodeGuids ?? []),
      ...(props.inputs.categoryNodeGuids ?? []),
    ];
    pendingAutoSubmit.value = true;
  }

  window.addEventListener("keydown", handleKeyDown);

  autoStartTour3();
});
onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyDown);
});

const pendingAutoSubmit = ref(false);
const stopAutoSubmit = watchEffect(async () => {
  if (pendingAutoSubmit.value && ruleFormRef.value) {
    pendingAutoSubmit.value = false;
    stopAutoSubmit();
    await submit(ruleFormRef.value);
  }
});

const handleKeyDown = (e: KeyboardEvent) => {
  if (e.ctrlKey || e.altKey) {
    return;
  }

  if (!currentTour.value) {
    return;
  }

  switch (e.key) {
    case "f": // for Finish
    case "n": // for Next and Finish
      changeTourStep(currentTour, 1);
      break;
    case "p":
      changeTourStep(currentTour, -1);
      break;
  }
};

const calcInputs = computed<CalcInputs>(() => {
  const { sectorGuids, categoryGuids } = splitSelectedSectors();
  return {
    isDouble: ruleForm.couple || false,
    age1: ruleForm.age ?? 0,
    income1: ruleForm.income ?? 0,
    age2: ruleForm.otherAge ?? 0,
    income2: ruleForm.otherIncome ?? 0,
    recaptchaResponse: "",
    sysOrgGuid:
      myInfoStore.currentSysOrgGuid ?? "F859AAA3-2A53-42C3-BD1D-4CF1E05DE4CD",
    sectorNodeGuids: sectorGuids,
    categoryNodeGuids: categoryGuids,
  };
});

const calcInputsForLocations = ref();
const showFiltersInCalc = ref(true);
const submit = async (formEl: FormInstance | undefined) => {
  calcInputsForLocations.value = calcInputs.value;
  if (!formEl) {
    showError("No form element provided");
    return;
  }
  await formEl.validate(async (valid) => {
    if (valid) {
      loading.value = true;
      const inputs = calcInputs.value;

      // const key = envConfig.recaptchaSiteKey;
      // const token = await window.grecaptcha.enterprise.execute(key, {
      //   action: "CALC",
      // });
      // inputs.recaptchaToken = token;
      activeInputs.value = JSON.stringify(inputs);

      if (showFiltersInCalc.value) {
        // housingFilters.value.facilityTypeCheckList.push(
        //   ruleForm.facilityTypeInitial!,
        // );
      }

      const rawList = await apiClient.calc.runCalculation(inputs);
      const fullList = rawList.map(extendResult);

      fullList.sort((a, b) => {
        if (a.site?.name !== undefined && b.site?.name !== undefined) {
          return a.site?.name.localeCompare(b.site?.name);
        }
        return 0;
      });

      results.value = fullList;

      // this will save the finder inputs to the cache if the user isn't logged in
      saveFinderInputs();
      if (props.application) {
        saveSitesQualifiedFor(results.value);
        emit("updateSelection", favoritesList.value);
      }

      surveyCompleted.value = true;
      isAllResults.value = false;
      showFiltersInCalc.value = false;

      filtersAccordionState.value = []; // close the filters window (mobile-only)
      loading.value = false;
    }
  });
};

const extendResult = (result: FinderResultDto): ResultPlus => {
  const resultSite = housingSiteStore.getSite(result.siteCode);
  const resultDetails = housingSiteDetailStore.List.filter((d) =>
    resultSite?.detailGuids.includes(d.guid),
  );
  return {
    ...result,
    site: resultSite,
    remaining: result.monthlyDisposalIncome,
    resultsIncluded: true,
    hmbName: resultSite?.housingOrgCode
      ? housingOrgStore.getNameByCode(resultSite.housingOrgCode)
      : "Unknown",
    details: resultDetails,
  };
};

const saveFinderInputs = () => {
  finderInputs.value.age1 = calcInputs.value.age1!;
  finderInputs.value.income1 = calcInputs.value.income1!;

  finderInputs.value.locations = housingFilters.value.locationCheckList;
  finderInputs.value.housingOrgs = housingFilters.value.housingOrgChecklist;
  // V2 holds UnitType leaf-node guids. Legacy `roomPreference` (string codes)
  // is left untouched — backfill controller migrates old rows.
  finderInputs.value.roomPreferenceV2 = housingFilters.value.roomTypeChecklist;

  const { sectorGuids, categoryGuids } = splitSelectedSectors();
  finderInputs.value.sectorNodeGuids = sectorGuids;
  finderInputs.value.categoryNodeGuids = categoryGuids;

  finderInputs.value.buildingTypes = housingFilters.value.buildingTypeChecklist;
  finderInputs.value.buildingAttributes = [
    ...new Set([
      ...housingFilters.value.buildingAttributeChecklist,
      ...hiddenBuildingAttrGuids.value,
    ]),
  ];
  finderInputs.value.accessible = housingFilters.value.accessibleOnly;

  if (calcInputs.value.isDouble) {
    finderInputs.value.age2 = calcInputs.value.age2;
    finderInputs.value.income2 = calcInputs.value.income2;
  } else {
    finderInputs.value.age2 = null;
    finderInputs.value.income2 = null;
  }

  if (props.inputs) {
    emit("updateFinderInfo", finderInputs.value);
  } else {
    emit("saveFinderInfo", finderInputs.value);
  }
};

/**
 * iterates through the list of results and maps them to a list of HousingSiteResult
 * and emits the list to be saved in the users housing application
 * @param list a list of sites
 */
const saveSitesQualifiedFor = async (list: ResultPlus[]) => {
  const sitesQualifiedFor: HousingSiteResult[] = [];

  list.forEach((site) => {
    const result: HousingSiteResult = {
      hmb: site.site?.housingOrgCode ?? "Unknown",
      siteCode: site.siteCode ?? "Unknown",
      unitType: site.eligibleUnits?.toLocaleString() ?? "",
      resultType: HousingSiteResultType.QUALIFIED,
      housingSiteUnitConfigurationGuids:
        site.housingSiteUnitConfigurationGuids ?? [],
    };

    // Push the initial result into the array
    sitesQualifiedFor.push(result);

    // Create and push a new result for each below market unit the user is eligible for
    site.eligibleBMPctCodes?.forEach((code) => {
      const bmResult: HousingSiteResult = {
        ...result,
        unitType: `${code.percentage}%${code.indexedTo}${code.roomType}`,
      };
      sitesQualifiedFor.push(bmResult);
    });
  });

  emit("updateSitesQualifiedFor", sitesQualifiedFor);
};

watch(
  () => housingFilters.value,
  () => {
    saveFinderInputs();
  },
  { deep: true },
);

watch(selectedSectors, () => saveFinderInputs(), { deep: true });

const selectedSectorLabels = computed<string[]>(() => {
  const labels: string[] = [];
  for (const guid of selectedSectors.value) {
    const node = sectorFlatNodeMap.value.get(guid);
    if (!node) continue;
    const name = node.finderSettings?.displayName || node.name;
    const parent = node.parentTreeNodeGuid
      ? sectorFlatNodeMap.value.get(node.parentTreeNodeGuid)
      : null;
    const parentName = parent?.finderSettings?.displayName;
    labels.push(parentName ? `${parentName}: ${name}` : name);
  }
  return labels;
});
const filterResults = (hf: HousingFilters): ResultPlus[] => {
  const locationsFiltered = hf.locationCheckList.length !== 0;
  const housingOrgsFiltered = hf.housingOrgChecklist.length !== 0;
  const quadrantsFiltered = hf.quadrantCheckList.length !== 0;
  const favoriteFiltered = hf.favoriteChecklist !== false;
  const buildingTypeFiltered = (hf.buildingTypeChecklist?.length ?? 0) !== 0;
  const buildingAttrFiltered =
    (hf.buildingAttributeChecklist?.length ?? 0) !== 0;

  return results.value.filter((loc: ResultPlus) => {
    if (
      favoriteFiltered &&
      favoritesList.value.includes(loc.site?.code ?? "")
    ) {
      // always include favorited sites if the favorite filter is on, regardless of other filters
      return true;
    }
    if (housingOrgsFiltered) {
      if (!hf.housingOrgChecklist.includes(loc.site?.housingOrgCode ?? "")) {
        return false;
      }
    }
    if (locationsFiltered) {
      const matchesLocation =
        hf.locationCheckList.includes(loc.site?.location ?? "") ||
        hf.locationCheckList.includes(loc.site?.geoTreeNodeGuid ?? "");
      if (!matchesLocation) {
        return false;
      }
    }
    if (quadrantsFiltered) {
      if (!hf.quadrantCheckList.includes(loc.site?.quadrant ?? "")) {
        return false;
      }
    }

    if (buildingTypeFiltered) {
      if (
        !hf.buildingTypeChecklist.includes(loc.site?.buildingTypeNodeGuid ?? "")
      ) {
        return false;
      }
    }

    if (buildingAttrFiltered) {
      const siteAttrGuids = loc.site?.buildingAttributeNodeGuids ?? [];
      const hasMatch = hf.buildingAttributeChecklist.every((guid) =>
        siteAttrGuids.includes(guid),
      );
      if (!hasMatch) return false;
    }

    if (hf.accessibleOnly) {
      const hasAccessible = loc.site?.unitConfigurations?.some(
        (uc) => uc.accessible,
      );
      if (!hasAccessible) return false;
    }

    // filter out results that don't match the selected V2 room types
    return hf.roomTypeChecklist.some((leafGuid) => {
      return loc.eligibleUnitTypeLeafNodeGuids?.includes(leafGuid) ?? false;
    });
  });
};

const resultsFiltered = computed(() => {
  return sortResults(filterResults(housingFilters.value));
});

const validLocationNames = computed<string[]>(() => {
  const result = filterResults({
    ...housingFilters.value,
    quadrantCheckList: [],
    locationCheckList: [],
  })
    .map((r) => r.site?.location || "")
    .filter((s) => s);
  return result;
});

const validLocationGuids = computed<string[]>(() => {
  return filterResults({
    ...housingFilters.value,
    quadrantCheckList: [],
    locationCheckList: [],
  })
    .map((r) => r.site?.geoTreeNodeGuid || "")
    .filter((s) => s);
});

const sortResults = (tempResults: ResultPlus[]) => {
  if (housingFilters.value.sortingChosen === sortNameA) {
    return tempResults.sort((a, b) => {
      if (a.site?.name !== undefined && b.site?.name !== undefined) {
        return a.site?.name.localeCompare(b.site?.name);
      }
      return 0;
    });
  } else if (housingFilters.value.sortingChosen === sortRentLow) {
    return tempResults.sort((a, b) => {
      if (a.finalRent !== undefined && b.finalRent !== undefined) {
        return a.finalRent - b.finalRent;
      }
      return 0;
    });
  } else if (housingFilters.value.sortingChosen === sortRentHigh) {
    return tempResults.sort((a, b) => {
      if (a.finalRent !== undefined && b.finalRent !== undefined) {
        return b.finalRent - a.finalRent;
      }
      return 0;
    });
  }
  return tempResults;
};

watchEffect(() => {
  const tempFilters = { ...housingFilters.value, locationCheckList: [] };
  const fullList = filterResults(tempFilters);
  const disabledLocations: LocationInfo[] = [];

  locations.value.forEach((loc) => {
    loc.disabled = !fullList.some((el) => el.site?.location === loc.location);
    if (loc.disabled) {
      disabledLocations.push(loc);
    }
  });

  // we don't need to remove any locations from the location checklist
  // housingFilters.value.locationCheckList =
  //   housingFilters.value.locationCheckList.filter((checkLocation) => {
  //     return !disabledLocations.some((loc) => loc.location === checkLocation);
  //   });
});

const sortLocationNames = () => {
  locations.value.sort((a, b) => {
    if (a.quadrant > b.quadrant) {
      return 1;
    } else if (a.quadrant < b.quadrant) {
      return -1;
    }

    if (a.location > b.location) {
      return 1;
    } else if (a.location < b.location) {
      return -1;
    } else {
      return 0;
    }
  });
};

const checkFavorite = (r: ResultPlus) => {
  if (favoritesList.value.includes(r.site?.code ?? "")) {
    return true;
  }
  return false;
};

const favoriteSelected = (
  favorite: boolean,
  siteCode: string,
  inBatch: boolean,
) => {
  if (favorite) {
    favoritesList.value.push(siteCode);
  } else {
    favoritesList.value = favoritesList.value.filter((el) => el !== siteCode);
  }

  if (!inBatch) {
    emit("updateSelection", favoritesList.value);
    emit("confirmSelection");
  }
};

const optionsList = ref<any[]>([]);
const optionsSelected = (
  selectedOptions: CheckboxGroupValueType,
  siteCode: string,
) => {
  if (selectedOptions.length > 0) {
    optionsList.value = optionsList.value.filter(
      (el) => el.startsWith(siteCode) === false,
    );
    optionsList.value = optionsList.value.filter(
      (el) => el.startsWith(siteCode) === false,
    );
    optionsList.value.push(`${siteCode}_${selectedOptions}`);
  } else {
    optionsList.value = optionsList.value.filter(
      (el) => el.startsWith(siteCode) === false,
    );
  }
};

// const resultsBeingSaved = ref<ResultPlus[]>();
// const openCount = ref(0);
// const sendToSilvera = ref(false);

// opens the dialog to update details
// const contactMe = (r: any, submittingRequest: boolean) => {
//   let tempResults = Object.values(r) as ResultPlus[];
//   tempResults.sort((a, b) => {
//     if (a.site?.name != undefined && b.site?.name != undefined) {
//       return a.site?.name.localeCompare(b.site?.name);
//     }
//     return 0;
//   });
//   resultsBeingSaved.value = tempResults;
//   sendToSilvera.value = submittingRequest;
//   openCount.value++;

//   allFiltersApplied.value = [];
//   allFiltersApplied.value = allFiltersApplied.value
//     .concat(housingFilters.value.locationCheckList)
//     .concat(housingFilters.value.quadrantCheckList)
//     .concat(housingFilters.value.facilityTypeCheckList);

//   if (favoritesList.value.length) {
//     allFiltersApplied.value = allFiltersApplied.value.concat("Favorites");
//   }
//   if (housingFilters.value.accessibilityChecklist) {
//     allFiltersApplied.value = allFiltersApplied.value.concat("Accessibility");
//   }
//   if (housingFilters.value.petChecklist) {
//     allFiltersApplied.value = allFiltersApplied.value.concat("Pet");
//   }
// };

// const getOptions = async () => {
//   // const local = await apiClient.details.postApiPDetailsGetOptionsList();
//   // optionsData.value = local;
// };

// const printResults = () => {
//   window.print();
// };

//  go through all the filtered locations
//  if they are not already in the favorites list, add them
const handleSelectAll = () => {
  favoritesList.value = [];

  resultsFiltered.value.forEach((loc) => {
    favoriteSelected(true, loc.site?.code ?? "", true);
  });

  emit("updateSelection", favoritesList.value);
  emit("confirmSelection");

  ElMessage.success(`Selected ${resultsFiltered.value.length} locations`);
};

//  remove all locations from the favorites list
const handleDeselectAll = () => {
  const currentFavourites = favoritesList.value;
  currentFavourites.forEach((loc) => {
    favoriteSelected(false, loc, true);
  });

  emit("updateSelection", favoritesList.value);
  emit("confirmSelection");
};

/** if true, only show the locations names */
const locationsCollapsed = ref(props.initialCollapseLocations ?? false);
/** if true, only show the selected locations */
const showOnlySelected = ref(props.initialShowOnlySelected ?? false);

const handleShowSelectedLocations = (val: boolean) => {
  housingFilters.value.favoriteChecklist = val;
};

// -------------------------------------
// Tour related

type Tour = {
  num: number;
  currentStep: RemovableRef<number>;
  isCompleted: RemovableRef<boolean>;
  isOpen: Ref<boolean>;
  requested: Ref<boolean>;
};

const numTours = 3;
const tours: Tour[] = Array.from({ length: numTours + 1 }, (_, i) => {
  // not using the 0th item to make the index match the tour number
  // will result in a dummy #0 in storage
  return {
    num: i,
    currentStep: useStorage(`tour_finder_${i}_step`, 0),
    isCompleted: useStorage(`tour_finder_${i}_done`, false),
    isOpen: ref(false),
    requested: ref(false),
  };
});

watch(
  () => surveyCompleted.value,
  () => autoStartTour3(),
);

const currentTour = computed(() => {
  return tours.find((tour) => tour.isOpen.value);
});

const startTour3 = () => {
  tours[3].currentStep.value = 0;
  tours[3].isOpen.value = true;
};
const autoStartTour3 = () => {
  // for now, only start tour 3 if the user has completed the survey but not selected any locations
  if (tour3Needed.value) {
    tours[3].currentStep.value = 0;
    tours[3].isOpen.value = true;
  }
};
const tour3Needed = computed(() => {
  return (
    surveyCompleted.value &&
    props.application !== undefined &&
    !favoritesList.value.length &&
    resultsFiltered.value.length
  );
});

const startTour = () => {
  if (surveyCompleted.value) {
    tours[2].isOpen.value = true;
  } else {
    tours[1].isOpen.value = true;
  }
};

const tourClosed = (tourNum: number, current: number) => {
  const tour = tours[tourNum];
  tour.isOpen.value = false;
  tour.currentStep.value = current;
};

const tourFinished = (tourNum: number) => {
  const tour = tours[tourNum];
  tour.isOpen.value = false;
  tour.isCompleted.value = true;
  if (tourNum === 2) {
    // if doing tour 2, start tour 3 after (if needed)
    autoStartTour3();
  }
  setTimeout(() => {
    // reset the step to 0 after the tour is closed
    // must do manually because the tour component sets it to the last step when finishing
    tour.currentStep.value = 0;
  }, 0);
};
const changeTourStep = (tour: Ref<Tour | undefined>, direction: number) => {
  if (!tour.value) {
    return;
  }
  const current = tour.value.currentStep.value;
  const newStep = current + direction;
  let numSteps = 0;
  switch (tour.value.num) {
    case 1:
      // Cannot dynamically get the number of steps from the tour component!
      // Tried with ref() but doesn't work: numSteps = tour1ref.value.$el.children.length;
      numSteps = 6;
      break;
    case 2:
      numSteps = 8;
      break;
    case 3:
      numSteps = 4 - (resultsFiltered.value.length ? 0 : 1);
      break;
  }
  if (direction === 1 && newStep >= numSteps) {
    tourFinished(tour.value.num);
    return;
  }
  if (newStep < 0) {
    return;
  }
  tour.value.currentStep.value = newStep;
};
</script>
<template>
  <div
    v-loading="loading"
    :class="['FinderPage', surveyCompleted ? 'withResults' : null]"
  >
    <div v-if="!surveyCompleted && props.application" class="finder-heading">
      <h2>Select Properties</h2>
      <div>
        Start selecting properties by answering the questions below, then click
        "Show Properties".
      </div>
    </div>
    <div v-if="!surveyCompleted" class="finder calculator-box centerCalc">
      <el-form
        ref="ruleFormRef"
        :model="ruleForm"
        :rules="rules"
        label-width="120px"
        label-position="top"
      >
        <div
          class="location-listing__filter-heading sort-by q1"
          :title="version"
        >
          Type of Accommodation
        </div>
        <el-form-item prop="couple" class="formOptions"
          ><el-radio-group
            v-model="ruleForm.couple"
            fill="#ff8e2b"
            class="filterGroup formOptions"
          >
            <el-radio
              :label="false"
              :value="false"
              :class="ruleForm.couple ? 'single-radio' : ''"
              >Single</el-radio
            >
            <el-radio :label="true" :value="true">Double</el-radio>
          </el-radio-group>
        </el-form-item>
        <div class="location-listing__filter-heading sort-by q2">Age</div>
        <el-form-item prop="age" class="age1">
          <el-input-number
            v-model="ruleForm.age"
            :controls="false"
            :precision="0"
            @blur="ageBlurred"
          />
        </el-form-item>
        <div
          v-if="ruleForm.couple"
          class="location-listing__filter-heading sort-by q3"
        >
          Co-applicant's Age
        </div>
        <el-form-item
          v-if="ruleForm.couple"
          id="partner-label"
          class="ageInput age2"
          prop="otherAge"
        >
          <el-input-number
            v-model="ruleForm.otherAge"
            class="ageInput"
            :controls="false"
            :precision="0"
            @blur="ageBlurred"
          />
        </el-form-item>
        <div class="combinedError">
          {{ combinedError }}
        </div>
        <div class="location-listing__filter-heading sort-by q4">
          Annual Income
        </div>
        <div class="line150">
          From line 15000 of most recent Notice of Assessment
        </div>

        <el-form-item prop="income" class="income1">
          <el-input-number
            v-model="ruleForm.income"
            class="incomeInput"
            :controls="false"
            :precision="0"
          />
        </el-form-item>
        <div
          v-if="ruleForm.couple"
          class="location-listing__filter-heading sort-by q5"
        >
          Co-applicant's Annual Income
        </div>
        <el-form-item
          v-if="ruleForm.couple"
          id="partner-label"
          class="incomeInput income2"
          prop="otherIncome"
        >
          <el-input-number
            v-model="ruleForm.otherIncome"
            :controls="false"
            :precision="0"
          />
        </el-form-item>

        <div
          v-if="sectorTreeData?.nodes?.length"
          v-loading="sectorLoading"
          class="calc-sector-finder"
        >
          <div class="location-listing__filter-heading">Sector Preferences</div>
          <SectorFinder
            v-model="selectedSectors"
            :root-nodes="sectorTreeData.nodes"
            root-node-name="Seniors Self-Contained"
          />
        </div>
      </el-form>

      <div class="findButtonDiv">
        <el-button
          type="info"
          :disabled="
            !!(combinedHasMoreErrors || combinedError) ||
            (!!sectorTreeData?.nodes?.length && selectedSectors.length === 0)
          "
          @click.stop="submit(ruleFormRef)"
        >
          Show Properties
        </el-button>
      </div>
    </div>
    <div v-else class="results">
      <div class="background"></div>

      <div class="filter-box biggerScreen hidden-on-print">
        <FilterBox
          v-if="surveyCompleted"
          v-model="housingFilters"
          v-model:survey-completed="surveyCompleted"
          :rule-form="ruleForm"
          :quadrants="quadrants"
          :locations="locations"
          :valid-location-names="validLocationNames"
          :valid-location-guids="validLocationGuids"
          :sorting-list="sortingList"
          :room-options="roomOptions"
          :building-options="buildingOptions"
          :building-attribute-options="buildingAttributeOptions"
          :application-in-progress="!!application"
          :is-read-only="isReadOnly"
          :selected-sector-labels="selectedSectorLabels"
          @clear-filters="clearFilters"
        />
      </div>

      <el-collapse
        v-if="surveyCompleted"
        v-model="filtersAccordionState"
        accordion
        class="hidden-on-print filters"
      >
        <el-collapse-item name="1">
          <template #title>
            <div class="filters-header">
              <el-icon-operation v-if="!filterIsExpanded" />
              <el-icon-close v-if="!filterIsExpanded" />
              View Search Filters
            </div>
          </template>

          <FilterBox
            v-if="surveyCompleted"
            v-model="housingFilters"
            v-model:survey-completed="surveyCompleted"
            :rule-form="ruleForm"
            :quadrants="quadrants"
            :locations="locations"
            :valid-location-names="validLocationNames"
            :valid-location-guids="validLocationGuids"
            :sorting-list="sortingList"
            :room-options="roomOptions"
            :building-options="buildingOptions"
            :building-attribute-options="buildingAttributeOptions"
            :application-in-progress="!!application"
            :selected-sector-labels="selectedSectorLabels"
          />
        </el-collapse-item>
      </el-collapse>

      <div ref="resultsHeaderRef" class="results-header hidden-on-print">
        <div class="result-count">
          <h2 class="result-total">
            Results {{ resultsFiltered.length }}
            {{
              resultsFiltered.length !== results.length
                ? `of ${results.length}`
                : ""
            }}
          </h2>
          <span class="result-selected">
            {{
              favoritesList.length ? ` (${favoritesList.length} selected)` : ""
            }}
          </span>
        </div>

        <el-button
          v-if="!application"
          class="selectAll"
          type="info"
          @click="emit('apply')"
        >
          Apply Now
        </el-button>

        <el-button
          v-if="application && !isReadOnly"
          class="selectAll"
          text
          bg
          @click="handleSelectAll"
        >
          Select All
        </el-button>

        <el-button
          v-if="application && !isReadOnly"
          :style="{
            visibility:
              application && favoritesList.length ? 'visible' : 'hidden',
          }"
          class="deselectAll"
          text
          bg
          @click="handleDeselectAll"
        >
          Deselect All
        </el-button>

        <!-- TODO: do we need print? can we remove it? -->
        <!-- <el-button
          v-if="surveyCompleted"
          class="printButton"
          :icon="Printer"
          circle
          size="large"
          @click="printResults"
        >
        </el-button> -->

        <el-checkbox
          v-if="application"
          v-model="locationsCollapsed"
          class="hide-checkbox"
          label="Hide Details"
        />

        <el-checkbox
          v-if="application && !isReadOnly"
          v-model="showOnlySelected"
          class="show-checkbox"
          @change="handleShowSelectedLocations(showOnlySelected)"
        >
          Show Selected
        </el-checkbox>
        <el-icon
          v-if="
            !myInfoStore.isPortal &&
            application &&
            application.source ===
              HousingApplicationSource.SENIOR_APPLICATION &&
            application.status === HousingApplicationStatus.DRAFT
          "
          class="tourIcon tourIcon3"
          title="Click to learn about selecting properties"
          @click="startTour3"
        >
          <el-icon-info-filled />
        </el-icon>
      </div>
      <!--header for printing-->
      <div v-if="tempHide" class="show-on-print">
        <p>
          Locations printed from silvera.ca on
          {{ formatDate(new Date().toLocaleDateString(), "date-only") }}
        </p>
        <p
          v-if="
            roomOptions &&
            roomOptions[0] &&
            JSON.stringify(Object.keys(roomOptions[0])) ===
              JSON.stringify(['4'])
          "
          class="contact-message"
        >
          We would love to speak with you about your housing options with
          Silvera for Seniors. Please call us at
          <a href="tel:403-240-4920">403.240.4920</a> or send an email to
          <a href="mailto:westviewliving@silvera.ca"
            >westviewliving@silvera.ca.</a
          >
        </p>
        <p v-else class="contact-message">
          We would love to speak with you about your housing options with
          Silvera for Seniors. Please call us at
          <a href="tel:403-567-5301">403-567-5301</a> or send an email to
          <a href="mailto:communityliving@silvera.ca"
            >communityliving@silvera.ca.</a
          >
        </p>
        <p>
          <i>
            Disclaimer: The information provided on this website is for general
            purposes and is an estimate only. We do not guarantee the accuracy
            or completeness of the information. Users are advised to
            independently verify details before making decisions. Please speak
            to a representative to confirm information. We are not liable for
            any losses or damages incurred from the use of this website.
            {{ version }}
          </i>
        </p>
      </div>

      <!-- list of locations -->
      <div
        v-if="surveyCompleted"
        class="resultList"
        :class="filterIsExpanded ? 'filters-expanded' : ''"
      >
        <!-- show if the user is only eligible for market housing -->
        <!-- <div
          v-if="
            roomOptions &&
            JSON.stringify(Object.keys(roomOptions[0])) ===
              JSON.stringify(['4']) &&
            resultsFiltered.length > 0
          "
          class="marketHousingMessage hidden-on-print"
        >
          We would love to speak with you about your housing options with
          Silvera for Seniors. Please submit a request for us to contact you
          (top right) so we can reach out to you directly. If you would prefer
          to contact us, please call us at
          <a href="tel:403-240-4920">403.240.4920</a> or send an email to
          <a href="mailto:westviewliving@silvera.ca"
            >westviewliving@silvera.ca.</a
          >
        </div> -->
        <Location
          v-for="r in resultsFiltered"
          :key="r.site?.code ?? ''"
          class="keep-on-print"
          :result="r"
          :inputs="calcInputsForLocations"
          :check-favorite="checkFavorite(r)"
          :application-in-progress="!!application"
          :collapsed="locationsCollapsed"
          :is-read-only="isReadOnly ?? false"
          @click:favorite="favoriteSelected"
          @click:option="optionsSelected"
        />

        <!-- if there are no locations -->
        <div v-if="!resultsFiltered.length" class="noResults">
          <i class="mdi mdi-home-group"></i>

          <div v-if="housingFilters.favoriteChecklist">
            <p>You have not selected any locations!</p>
            <p>Uncheck 'Show Selected' to view all eligible locations.</p>
          </div>
          <div v-else>
            <!-- <p v-if="ageQualificationNotMet">
              Although you do not yet meet the age qualification for rent geared
              to income, affordable independent living with Silvera, help is
              available. Please contact one or more of the resources on this
              page for assistance:
              <a
                href="https://www.silvera.ca/age-qualification/"
                target="_blank"
              >
                https://www.silvera.ca/age-qualification/ </a
              >.
            </p> -->
            <p>Please adjust your filters to see available locations.</p>
            <!-- <p v-if="!resultsFiltered.length">
              Please see our
              <a href="https://www.silvera.ca/contactus/" target="_blank">
                Contact Page
              </a>
              to find our phone number and discuss your situation.
            </p> -->
          </div>
        </div>
      </div>
    </div>

    <el-tour
      ref="tour1"
      v-model="tours[1].isOpen.value"
      :current="tours[1].currentStep.value"
      @change="(step: number) => (tours[1].currentStep.value = step)"
      @close="(step: number) => tourClosed(1, step)"
      @finish="tourFinished(1)"
    >
      <el-tour-step
        target=".finder"
        title="Welcome to the Housing Finder!"
        placement="top"
        description="Here is where you can tell us a little about yourself and what you are looking for. We will use this information to find the best properties for you."
      />
      <el-tour-step
        target=".formOptions"
        title="Type of Accomodation"
        placement="right"
        description="Are you applying for yourself or someone else? Select the option that best fits your situation."
      />
      <el-tour-step
        target=".age1 .el-input"
        title="Age"
        placement="right"
        description="How old are you? This will help us find properties that are suitable for your age group."
      />
      <el-tour-step
        target=".income1 .el-input"
        title="Annual Income"
        placement="right"
        description="What is your annual income? This is important for determining your eligibility for certain properties. You can give an approximate amount here, but we'll need the exact number when you complete your form."
      />
      <el-tour-step
        target=".calc-filters"
        title="Filters?"
        placement="right"
        description="Do you have any special requirements? Use the filters to narrow down your search. You can easily change these later."
      />
      <el-tour-step
        target=".findButtonDiv button"
        title="Click 'Show Properties'"
        placement="right"
        description="We will get the list of properties that fit your criteria."
      />
    </el-tour>
    <el-tour
      ref="tour2"
      v-model="tours[2].isOpen.value"
      :current="tours[2].currentStep.value"
      @change="(step: number) => (tours[2].currentStep.value = step)"
      @close="(step: number) => tourClosed(2, step)"
      @finish="tourFinished(2)"
    >
      <el-tour-step
        target=".results-header"
        title="Property List"
        placement="top"
        description="Below is the list of properties that match your criteria. You can select properties that you are interested in."
      />
      <el-tour-step
        target=".filterGroup:nth-child(3)"
        title="Additional Filters"
        placement="right"
        description="You can filter the list of properties by additional criteria for Room Options, Housing Organizations, and Locations."
      ></el-tour-step>
      <el-tour-step
        target=".location:nth-child(1) .location-header"
        title="Property Details"
        placement="top"
        description="This is the detailed information about the property."
      ></el-tour-step>
      <el-tour-step
        target=".location:nth-child(1) .location-facility-features"
        title="Features"
        placement="left"
        description="This list of features can be expanded for more details."
      ></el-tour-step>
      <el-tour-step
        target=".location:nth-child(1) .packageInfo"
        title="Base Costs"
        placement="left"
        description="The base costs for the property are shown here."
      ></el-tour-step>
      <el-tour-step
        target=".location:nth-child(1) .options"
        title="Optional Costs"
        placement="left"
        description="These are available options that you can choose as you explore these properties."
      ></el-tour-step>
      <el-tour-step
        target=".location:nth-child(1) .rent"
        title="Total Rent"
        placement="left"
        description="The total rent for the property is shown here, including any options you might choose."
      ></el-tour-step
      ><el-tour-step
        target=".results-header"
        title="More Properties"
        placement="top"
        description="Scroll down the page to see all the properities!"
      ></el-tour-step>
    </el-tour>
    <el-tour
      v-if="!myInfoStore.isPortal"
      ref="tour3"
      v-model="tours[3].isOpen.value"
      :current="tours[3].currentStep.value"
      @change="(step: number) => (tours[3].currentStep.value = step)"
      @close="(step: number) => tourClosed(3, step)"
      @finish="tourFinished(3)"
    >
      <el-tour-step
        v-if="resultsFiltered.length"
        target=".location:nth-child(1) .favorite"
        title="Selecting a Property"
        placement="left"
        description="To proceed with your application, you will need to select each property that you are interested in by clicking the checkbox for each."
      />
      <el-tour-step
        target=".results-header .selectAll"
        title="Take them all?"
        placement="left"
        description="If you want to select all the properties (that are currently showing), click the 'Select All' button."
      />
      <el-tour-step
        target=".results-header .show-checkbox"
        title="Show Selected"
        placement="bottom"
        description="You can filter the list to show only the properties you have selected."
      />
      <el-tour-step
        target=".el-step:nth-child(1) .el-step__description span"
        title="Number Selected"
        placement="bottom"
        description="You need to have some selected before you can submit your application."
      />
    </el-tour>
    <el-icon
      class="tourIcon tourIcon1_2"
      title="View a quick tour of the Housing Finder"
      @click="startTour"
    >
      <el-icon-info-filled />
    </el-icon>
  </div>
</template>

<style lang="less">
.FinderPage {
  margin-bottom: 100px;

  &.withResults {
    width: 100%;
  }

  .background {
    position: fixed;
    top: 3em;
    // subtract the width of the scrollbar
    width: calc(100% - 44px);
    height: 12em;
    background-color: var(--yych-background-color);
    z-index: 2;
  }

  .tourIcon {
    cursor: pointer;
    path {
      fill: var(--el-color-info);
    }

    &::after {
      content: "";
      position: absolute;
      height: 80%;
      width: 80%;
      margin: 10% auto;
      border-radius: 50%;
      z-index: -1;
      background-color: var(--el-color-info-light-9);
    }
  }
  .tourIcon1_2 {
    position: fixed;
    bottom: 0.5em;
    right: 1em;
    font-size: 2em;
  }
  .tourIcon3 {
    vertical-align: top;
    grid-area: tour3;
    margin-left: -10px;
  }

  @media print {
    .tourIcon {
      display: none;
    }
  }

  h4 {
    font-size: 1.4rem;
    margin: 0;
    // font-weight: bold;
  }
  // these are to improve visibility of the checkboxes
  .el-checkbox__input.is-disabled {
    text-decoration: line-through;
    .el-checkbox__inner {
      border-color: #d5d4d4;
    }
  }
  .el-checkbox__inner,
  .el-radio__inner {
    border-color: var(--borderColor);
    // border-color: #969696;
    border-width: 2px;
    height: 18px;
    width: 18px;
  }
  /* Center the check mark in the checkbox for all sizes.
     Use transform-based centering so it remains centered even if
     the checkbox dimensions change. */
  .el-checkbox__inner::after {
    left: 50% !important;
    top: 50% !important;
  }
  .el-radio__input.is-checked + .el-radio__label {
    color: var(--el-checkbox-text-color);
  }

  .show-on-print {
    display: none;
  }

  .location-listing__filter-heading {
    color: var(--subheading-color);
    margin: 0.5em 0 0.25em 0;
    font-size: 1.3rem;
    font-weight: 500;
  }

  .hide-calcInputs {
    opacity: 0;
  }

  .calculator-box {
    grid-area: calcBox;
    // box-shadow: #82675336 0px 0px 8px 4px;
    padding: 0.5em 2em 1em;
    background-color: var(--el-color-primary-light-9);

    max-width: 700px;
    grid-template-columns: 0 1fr 0;
    justify-items: center;

    .results-header,
    .filter-box {
      display: none;
    }

    .button {
      margin-top: 2em;
    }

    .hide-extra-filters {
      display: none;
    }

    .findButtonDiv {
      margin: 1em 0;
      text-align: center;
    }

    .calc-filters {
      label {
        margin-left: 1em;
        margin-top: 0.3em;
      }
    }
    .calc-sector-finder {
      margin-top: 0.5em;
    }
    .location-listing__filter-heading {
      margin-left: 0;
    }
    .el-form {
      display: grid;
      grid-template-areas:
        "q0 q0"
        "q1 q1"
        "formOptions formOptions"
        "q2 q3"
        "age1 age2"
        "combinedError combinedError"
        "q4 q5"
        "line150 line150"
        "income1 income2"
        "sector sector";
      grid-column-gap: 3em;

      .calc-sector-finder {
        grid-area: sector;
      }

      .el-form-item__content {
        margin-left: 1em;
      }

      .q0 {
        grid-area: q0;
      }
      .q1 {
        grid-area: q1;
        // white-space: nowrap;
      }

      .q2 {
        grid-area: q2;
      }

      .q3 {
        grid-area: q3;
      }

      .q4 {
        grid-area: q4;
        // white-space: nowrap;
        .el-icon {
          vertical-align: text-top;
        }
      }

      .q5 {
        grid-area: q5;
      }
      .line150 {
        grid-area: line150;
        font-size: 0.8em;
        color: #5a5858;
        margin: -0.5em 0 0.5em;
        font-style: italic;
      }

      .formOptions {
        grid-area: formOptions;
        display: flex;
        flex-direction: row;
        // flex-wrap: nowrap;
      }

      .age1 {
        grid-area: age1;

        .el-form-item__error {
          display: none;
        }
      }
      .combinedError {
        grid-area: combinedError;
        color: var(--el-color-danger);
        margin: -1em 0 0 1em;
        font-size: 80%;
        min-height: 1.5em;
      }

      .age2 {
        grid-area: age2;

        .el-form-item__error {
          display: none;
        }
      }

      .income1 {
        grid-area: income1;
      }

      .income2 {
        grid-area: income2;
      }

      .findButtonDiv {
        align-self: left;
      }
    }

    // .button {
    //   background-color: #0961a8;
    //   animation: glowing 1.25s infinite;
    // }
    // @keyframes glowing {
    //   0% {
    //     background-color: #3488ce;
    //     box-shadow: 0 0 3px #3488ce;
    //   }
    //   50% {
    //     background-color: #0961a8;
    //   }
    //   100% {
    //     background-color: #3488ce;
    //     box-shadow: 0 0 3px #3488ce;
    //   }
    // }

    .el-input {
      width: 7.5em;
      .el-input__wrapper {
        box-shadow: 0 0 0 0.5px gray;
      }
    }

    .el-form-item__content {
      display: block;
      line-height: inherit;

      .el-form-item__error {
        top: unset;
        position: relative;
        font-size: 1.2em;
        grid-area: "ageError";
      }
    }
    // }
  }

  .results {
    grid-area: results;
    display: grid;
    grid-template-columns: min-content 3fr;
    grid-template-rows: auto 1fr;
    grid-template-areas:
      "f  h"
      "f  r";

    grid-gap: 0 1em;
    .inputInfo {
      max-width: 400px;
      display: grid;
      grid-template-areas:
        "h h"
        "a1 a2"
        "i1 i2";

      grid-gap: 0.5em;
      margin: 0.15em 0 0 0;

      .h {
        grid-area: h;
      }
      .a1 {
        grid-area: a1;
      }
      .a2 {
        grid-area: a2;
      }
      .i1 {
        grid-area: i1;
      }
      .i2 {
        grid-area: i2;
      }
    }

    .sectorInfo,
    .inputInfo {
      color: #5a5858;
      font-size: 1.1rem;
      color: #5a5858;
    }

    .sectorInfo {
      display: flex;
      gap: 0.5em;
      margin-top: 0.5em;

      .sectorSelected {
        display: grid;
      }
    }
    .filter-box {
      grid-area: f;
      height: 68vh;
      border: 1px;
      padding: 1em 0.5em 3em 1.25em;
      // box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
      background: var(--yych-background-color-light);
      overflow-y: auto;
      box-sizing: content-box;
      position: sticky;
      top: 6.5em;
      left: 0;
      align-self: flex-start;
      z-index: 3;
      border-radius: 5px;

      /** this prevents scrolling the results once you've scrolled ot the end of the filter box */
      overscroll-behavior: none;
    }

    .singleFilter {
      .el-checkbox__label {
        font-weight: 450;
        // font-size: inherit;
      }
    }
    .filterGroup {
      display: grid;
      margin-bottom: 0.5em;
      columns: 1;

      .el-radio__label {
        font-weight: 450;
        font-size: inherit;
        margin-right: 0.5em;
      }

      .el-checkbox__label {
        font-weight: 450;
        color: var(--el-text-color-regular);
      }
    }

    .el-form-item {
      margin-bottom: 0px;
      margin-left: 1em;
    }

    .smallerScreen {
      display: none;
    }

    button:focus,
    button:focus-visible {
      outline: 4px auto transparent;
    }
    .el-collapse {
      position: sticky;
      top: 7.4em;
      z-index: 99; /* for the sticky */

      display: none;
      border-top: none;
      border-bottom: none;
      margin-bottom: 5px;

      .el-collapse-item__header {
        // position: sticky;
        // top: 0;
        z-index: 99; /* for the sticky */
        width: 100%;
        padding-left: 1em;
        color: rgb(92, 90, 90);
        font-size: var(--el-font-size-base);
        border-bottom: 2px solid var(--el-text-color-primary);
        border-radius: 8px;
        outline: none;
        background-color: var(--yych-background-color-light);

        .el-collapse-item__arrow {
          display: none;
        }

        .el-icon {
          margin-right: 0.6em;
        }
      }
      @media (max-width: 1025px) {
        .filters-header svg {
          font-size: 1em;
          width: 1em;
          height: 1em;
        }
      }
    }

    .results-header {
      grid-area: h;
      display: grid;
      grid-template-columns: 1fr repeat(5, auto);
      grid-template-areas: "header hide show tour3 deselect select";
      align-items: center;
      gap: 0 1em;
      color: #5a5858;
      padding: 1em;
      height: fit-content;
      position: sticky;
      top: 6.2em;
      background: var(--yych-background-color-light);
      z-index: 3;
      border-bottom: solid 0.5em var(--yyc-green);

      .show-checkbox {
        grid-area: show;
        justify-self: end;
      }

      .hide-checkbox {
        grid-area: hide;
        justify-self: end;
      }
      .deselectAll {
        grid-area: deselect;
      }
      .selectAll {
        grid-area: select;
      }
      .deselectAll,
      .selectAll {
        width: fit-content;
        justify-self: end;
      }

      @keyframes flashing {
        0% {
          background-color: var(--el-color-info);
        }
        50% {
          background-color: var(--el-color-primary);
        }
        100% {
          background-color: var(--el-color-info);
        }
      }

      .confirm-btn {
        grid-area: confirm;
        // margin-right: 1em;
        &.flashing-btn {
          animation: flashing 1s infinite;
        }
      }

      .result-count {
        grid-area: header;
        align-self: center;
        display: flex;
        align-items: center;
        gap: 1em;

        .result-total {
          margin: 0;
          font-weight: 600;
        }

        .result-selected {
          font-size: 1.2em;
        }
      }

      .emailButton {
        grid-area: email;
      }

      .contactButton {
        grid-area: contact;
        text-wrap: wrap;
        color: white;
        background-color: var(--el-text-color-primary);
      }

      .printButton,
      .selectAll,
      .deselectAll,
      .confirm-btn {
        align-self: center;
        font-size: 16px;
        font-weight: 700;
      }

      .printButton {
        grid-area: print;
        display: flex;
        width: 45px;
        height: 45px;
        font-size: 1.5em;
        justify-self: start;

        .el-icon {
          font-size: 20px;
        }
      }
    }

    .resultList {
      grid-area: r;
      // margin-top: 1em;

      .marketHousingMessage {
        padding: 1em;
        margin-bottom: 2em;
        font-size: 1.2em;
        font-weight: 500;
        box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
        border-radius: 5px;
      }

      .noResults {
        margin-left: 1em;
        text-align: center;
        font-size: 1.1em;
        margin-top: 5em;
        // height: calc(99vh - 95px);
        i {
          font-size: 1.4em;
          vertical-align: text-bottom;
        }

        i.mdi {
          font-size: 48px;
        }
      }
    }
  }

  @media (pointer: coarse) {
    .background {
      width: 100%;
    }
  }

  @media (max-width: 1450px) {
    .results {
      .results-header {
        grid-template-columns: 1fr repeat(3, auto);
        grid-template-areas: "header header header header header" "x hide show deselect select";
      }
    }
  }

  /* below 1025px, change the grid layout to have a collapsible filter at the top of the page */
  @media (max-width: 1025px) {
    .results {
      grid-template-columns: auto;
      grid-template-rows: auto auto auto;
      grid-template-areas:
        "fc"
        "h"
        "r";
      grid-gap: 0;

      div.results-header {
        // position: relative;
        top: 12.4em;
        font-size: 0.8em;
        position: static;
      }
      .resultList {
        .marketHousingMessage {
          margin-top: 1em;
        }
      }
      .background {
        display: none;
      }

      // .viewInputsBtn {
      //   grid-area: inputs;
      // }

      .smallerScreen {
        display: block;
      }

      .filters-header {
        display: flex;
        align-items: center;
        white-space: nowrap;
      }
      .filters-expanded {
        display: none; /* for when the filter menu is expanded, to prevent scrollbar from locations */
      }

      .inputInfo {
        font-size: 1.3em;
        margin-bottom: 0;
      }

      .biggerScreen {
        display: none;
      }

      .el-collapse {
        position: static;
        display: block;
        width: 100%;
        // margin: 1em 1em 0;
        &.finder {
          grid-area: calculator;
        }

        &.filters {
          grid-area: fc;
        }
        .el-tree-node {
          padding: 0.15em 0;
        }

        .el-tree-node__content {
          &:has(> .el-icon) {
            &:has(> .hide-icon) {
              // to maintain the same margin as the rest of the tree nodes
              margin-left: 21px;
            }
          }
        }

        .el-collapse-item__content {
          padding: 0 1em 1em 2em;
          background-color: #f6f6f6;

          .locationFilterGroup {
            display: grid;
          }
        }
      }
    }
  }

  /* below 640px move reset filters button because it gets too tight */
  @media (max-width: 700px) {
    .results {
      .results-header {
        grid-template-columns: max-content auto max-content;
        grid-template-areas:
          "header header header"
          "deselect y hide"
          "select y show";
        padding: 1em 0;
        gap: 6px;
        padding-left: 20px;

        .selectAll,
        .deselectAll,
        .confirm-btn {
          justify-self: start;
          margin: 0em;
        }

        .hide-checkbox {
          margin: 0;
        }

        .printButton {
          justify-self: start;
        }

        .location-listing__filter-heading {
          margin-top: 0;
          margin-left: 0;
        }
      }
    }
    .el-checkbox__label {
      font-size: 0.7em;
    }
  }

  @media (min-width: 500px) and (max-width: 660px) {
    .results {
      .results-header {
        font-size: 0.8em;
      }

      .resultList {
        .marketHousingMessage {
          font-size: 1em;
        }
      }
    }
  }

  @media screen and (max-width: 500px) {
    .filterGroup,
    .singleFilter {
      .el-checkbox__label,
      .el-radio__label {
        font-size: 0.7em;
        //text-wrap: wrap;
        line-height: 22px;
        white-space: normal;
      }
    }
    .results {
      .results-header {
        font-size: 0.75em;
        .el-button--large {
          padding: 0;
        }
      }
      .resultList {
        .marketHousingMessage {
          font-size: 1em;
        }
      }
    }
    .location {
      h3 {
        font-size: 1.4em;
      }
      .location-header .location-title {
        font-size: 1.75em;
      }
      .info {
        .description,
        .packageInfo,
        .rent,
        .remaining {
          font-size: 1.2em;
        }
        .description .el-link {
          font-size: 1em;
        }
      }
    }
  }

  @media print {
    margin: 0;
    .hidden-on-print {
      display: none !important;
    }

    .noResults {
      height: auto;
    }

    .primary-nav,
    section,
    footer,
    .testPageInfo {
      display: none !important;
    }

    .show-on-print {
      display: block;
      font-size: 14px;
    }

    .grecaptcha-badge,
    .grecaptcha-logo,
    .background {
      display: none !important;
    }

    .results {
      grid-template-columns: auto;
      grid-template-rows: auto auto;
      grid-template-areas:
        "h"
        "r";
    }

    .location {
      border: none;
      background: white;
      page-break-inside: avoid;
      h3 {
        font-size: 0.8em;
      }
      .location-header {
        margin-top: -10px;
        .location-title {
          font-size: 1em;
        }
      }

      .location-facility-type {
        .location-facility-features {
          font-size: 0.7em;
        }
        .neighborhood-quadrant {
          text-align: center;
          font-size: 0.8em;
        }
      }

      .disclaimer {
        font-size: 0.6em;
        margin-right: 60px;
      }

      .info {
        margin-left: -20px;
        .options .optionsList,
        .packageInfo,
        .rent,
        .remaining {
          font-size: 0.6em;
        }
        .description,
        .location .info .features {
          font-size: 0.6em;
          margin-left: 20px;
        }
        .location-image {
          display: inherit;
          margin-left: 160px;
          max-width: 400px;
        }
      }
    }
    .location:last-of-type {
      margin-bottom: 200vh;
    }
  }
}
</style>
